/* eslint-disable @typescript-eslint/no-explicit-any */
import { type SkuId, adjustDateToRules } from '@bluebrick/discounts-service-module'
import { cloneDeep } from 'lodash-es'
import { type DateTime } from 'luxon'
import { defineStore, storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import { isDateTimeBefore, now, setSourcesHourInTarget } from '@/common/dateUtils'
import { arraysHaveTheSameElements, encodeNotes, isCustomerCreatedEvent, isEventType, isOpportunityEvent } from '@/common/utils'
import { useActionsStore } from '@/features/actions/useActionsStore'
import { addEvent, removeEvent, updateEvent } from '@/features/eventDetails/eventDetailsRemoteRepository'
import { findNextSlot } from '@/features/eventDetails/eventDetailsUtils'
import { type EventDetailsMode } from '@/features/eventDetails/types'
import { useEventDetailsBaseStore } from '@/features/eventDetails/useEventDetailsBaseStore'
import { useEventDetailsCooldown } from '@/features/eventDetails/useEventDetailsCooldown'
import { useEventDetailsDatesStore } from '@/features/eventDetails/useEventDetailsDatesStore'
import { useEventDetailsDepthsStore } from '@/features/eventDetails/useEventDetailsDepthsStore'
import { useEventDetailsDurationStore } from '@/features/eventDetails/useEventDetailsDurationStore'
import { useEventDetailsProducts } from '@/features/eventDetails/useEventDetailsProducts'
import { useEventDetailsRemoteDataStore } from '@/features/eventDetails/useEventDetailsRemoteDataStore'
import { useEventDetailsSourceEvent } from '@/features/eventDetails/useEventDetailsSourceEvent'
import { useEventDetailsStoreData } from '@/features/eventDetails/useEventDetailsStoreData'
import { useEvent } from '@/features/events/useEvent'
import { useEventsStore } from '@/features/events/useEventsStore'
import { useEventNotesStore } from '@/features/notes/useEventNotesStore'
import { useNotificationsStore } from '@/features/notifications/useNotificationsStore'
import { useSaleDaysSummaryStore } from '@/features/saleDaysSummary/useSaleDaysSummaryStore'
import { useSidebarStore } from '@/features/sidePanel/useSidebarStore'
import { useTelemetry } from '@/features/telemetry/useTelemetry'
import { type Store, type EventData, EventType, type UpsertEventRequestBody, type SkuData } from '@/types'

export const useEventDetailsStore = defineStore('eventDetails', () => {
	const { trackEvent, trackException } = useTelemetry()
	const { notifySuccess, notifyError } = useNotificationsStore()
	const { isEventSidebarVisible } = storeToRefs(useSidebarStore())
	const { showEventSidebar, closeEventSidebar } = useSidebarStore()
	const eventNotes = useEventNotesStore()
	const { selectedSKUIdsForStore } = storeToRefs(useEventsStore())
	const { setSelectedSKUIds, loadSKUData, unselectAllSKUs, selectSKU, selectAllRelatedSkus, getMatchingEventForSkuId } = useEventsStore()
	const { indicatePendingRefresh, fetch: fetchActions } = useActionsStore()
	const { storesWithSummaryIds } = storeToRefs(useSaleDaysSummaryStore())
	const { fetch: fetchSaleDaysSummary } = useSaleDaysSummaryStore()
	const { store, storeId, startDate, endDate, name, sourceEvent, eventsRelatedToSourceEvent, mode } = storeToRefs(useEventDetailsBaseStore())
	const {
		updateStartDate: setStartDate,
		updateEndDate: setEndDate,
		updateStore,
		updateMode,
		updateSourceEvent,
		updateRelatedEvents,
		reset: resetBaseStore
	} = useEventDetailsBaseStore()

	const { recommendedStartDate, recommendedEndDate, isEventInThePast, eventDatesDraft } = storeToRefs(useEventDetailsDatesStore())
	const { updateRecommendedDates, updateEventTimes, setOptimalDuration, reset: resetDates } = useEventDetailsDatesStore()

	const { products } = useEventDetailsProducts()

	const { recommendedDepths } = storeToRefs(useEventDetailsDepthsStore())
	const { setCustomDepth, reset: resetDepths } = useEventDetailsDepthsStore()

	const {
		optimalDurationInDays,
		optimalEventDurationFormatted,
		maxDurationInDays,
		maxEventDurationFormatted,
		eventDuration,
		eventIntervalUTC,
		isOptimalEventDurationValid,
		isRecommendedEventDuration,
		isEventTooLong,
		isEventLongerThanOptimal
	} = storeToRefs(useEventDetailsDurationStore())
	const { updateRecommendedAndOptimalDurations, reset: resetDurations } = useEventDetailsDurationStore()

	const { discountConfig, eventDurations, nearestStartDate } = storeToRefs(useEventDetailsRemoteDataStore())
	const {
		fetchNearestStartDate,
		fetchDiscountConfig,
		fetchEventMaxAndOptimalLengths,
		fetchRecommendedDates,
		fetchRecommendedDepths,
		reset: resetRemoteData
	} = useEventDetailsRemoteDataStore()

	const { sourceOpportunity, sourceOpportunityDescription, isFromOpportunity, isFromStoreOpportunity, isFromMajorStoreOpportunity } =
		useEventDetailsSourceEvent()

	const { cooldownDuration, cooldownStart, cooldownStartRaw, cooldownEnd, cooldownEndRaw, isCooldownLengthValid, doesEventTriggerCooldown } =
		useEventDetailsCooldown()

	const { storeData, storeSKUs, selectedSkuIds, canCalculateEventParameters, sourceEventCooldowns, getEventsPayloads } = useEventDetailsStoreData()

	const isInitializingEvent = ref(false)
	const isSaving = ref(false)
	const type = ref<EventType>()

	const isReadonly = computed(() => !!sourceEvent.value && mode.value === 'view')

	// Indicates whether an event is being updated rather than created (source event exists and is not an opportunity)
	const isUpdateEvent = computed<boolean>(() => !isReadonly.value && !!sourceEvent.value?.id && !isOpportunityEvent(sourceEvent.value))

	const isScraped = computed(() => sourceEvent.value?.event_origin === 'scraped')

	/**
	 * If given store has only one SKU then automatically mark it as selected.
	 */
	function autoSelectLoneProduct() {
		if (storeSKUs.value.length === 1) {
			selectSKU(storeSKUs.value[0].unique_sku_id)
		}
	}

	/**
	 * If only single products is selected then returns first opportunity for that product.
	 */
	function findOpportunityForSingleProduct() {
		if (store.value && products.value.length === 1 && storeData.value) {
			return findNextSlot(storeData.value, products.value[0].uniqueSkuId, now())
		}
	}

	/**
	 * Resets the form to a state as if given opportunity was clicked on the timeline.
	 */
	function changeToOpportunity(opportunity: EventData) {
		if (store.value && products.value[0]) {
			createEventFromOpportunity(store.value, opportunity, products.value[0].uniqueSkuId)
		}
	}

	/**
	 * Creates payload and saves currently created or edited event.
	 */
	async function submit() {
		const affectedSKUIds: SkuId[] = []

		isSaving.value = true

		const allAsyncActions = products.value.map(async (product) => {
			const requestBody: UpsertEventRequestBody = {
				start_date: startDate.value?.toUTC().toISO() ?? undefined,
				end_date: endDate.value?.toUTC().toISO() ?? undefined,
				discount_depth: product.discountDepth,
				...(name.value === '' ? {} : { name: name.value }),
				notes: encodeNotes(eventNotes.notes) ?? []
			}
			const eventName = name.value || 'Your event'

			const sourceEventForSKUId = eventsRelatedToSourceEvent.value?.find((event) => product.uniqueSkuId === event.unique_sku_id)

			switch (mode.value) {
				case 'create':
					if (!store.value?.id) {
						notifyError(`Failed to add the event for ${product.humanName}.`)

						return
					}

					requestBody.opportunity_origin = sourceEvent.value

					try {
						await addEvent(store.value.id, product.uniqueSkuId, requestBody)

						notifySuccess(`${eventName} was added successfully for ${product.humanName}.`)

						affectedSKUIds.push(product.uniqueSkuId)
					} catch (error) {
						notifyError(`Failed to add the event for ${product.humanName}.`)

						trackException(error, `Failed to add the event for ${product.humanName}.`, { store: String(store.value) })
					}

					break
				case 'update':
					if (!sourceEventForSKUId?.id) {
						notifyError(`Failed to update the event for ${product.humanName}.`)

						return
					}

					// If we're updating an event that was a major store event then make sure
					// to retain the `major` and `triggers_cooldown` values
					requestBody.major = sourceEventForSKUId.major
					requestBody.triggers_cooldown = sourceEventForSKUId.triggers_cooldown ?? true

					try {
						await updateEvent(sourceEventForSKUId?.id, requestBody)

						notifySuccess(`${eventName} was updated successfully for ${product.humanName}.`)

						affectedSKUIds.push(sourceEventForSKUId.unique_sku_id)
					} catch (error) {
						notifyError(`Failed to update the event for ${product.humanName}.`)

						trackException(error, `Failed to update the event for ${product.humanName}.`, { store: String(store.value) })
					}

					break
			}
		})

		try {
			await Promise.allSettled(allAsyncActions)

			if (affectedSKUIds.length && store.value) {
				updateSKUData(affectedSKUIds, store.value)
			}

			closeEventSidebar()

			reset()
		} catch (error) {
			trackException(error, `Failed to ${mode.value} an event.`, { store: String(store.value) })

			switch (mode.value) {
				case 'create':
					notifyError('Failed to add the event.')

					break

				case 'update':
					notifyError('Failed to update the event.')

					break
			}
		} finally {
			isSaving.value = false
		}
	}

	async function deleteEvent() {
		const affectedSKUIds: SkuId[] = []

		const allAsyncActions = products.value.map(async (product) => {
			const event = eventsRelatedToSourceEvent.value?.find((event) => product.uniqueSkuId === event.unique_sku_id)

			if (!event) {
				notifyError(`Failed to remove event for ${product.humanName}.`)

				return
			}

			const eventName = event.name || 'Your event'

			if (!event?.id) {
				notifyError(`Failed to remove ${eventName}.`)

				return
			}

			await removeEvent(event.id)

			notifySuccess(`${eventName} was deleted successfully.`)

			affectedSKUIds.push(event.unique_sku_id)
		})

		await Promise.allSettled(allAsyncActions)

		if (affectedSKUIds.length && store.value) {
			updateSKUData(affectedSKUIds, store.value)
		}

		closeEventSidebar()

		reset()
	}

	/**
	 * When creating an event (from an opportunity or at an empty space in the timeline)
	 * use the clicked SKU together with every selected SKU (so if SKU "A" and "B" are selected
	 * and we click SKU "C", which is not selected then all three will be used in the event).
	 * In non-create mode we can view/edit an event for a single SKU only.
	 */
	function getSKUIdsForTheEvent(clickedSkuId: SkuId): SkuId[] {
		return [...selectedSKUIdsForStore.value(storeId.value), clickedSkuId]
	}

	/**
	 * Initializes basic properties of the event and then open the sidebar.
	 */
	function initSidebar(aStore: Store, mode: EventDetailsMode, eventType = EventType.Discount, eventName = '') {
		reset()

		updateMode(mode)

		updateStore(aStore)

		type.value = eventType
		name.value = eventName

		showEventSidebar()
	}

	/**
	 * Populates current event's data (selected SKU and dates) based on given source event.
	 */
	async function initEventBasedOnAnotherEvent(event: EventData, skuId: SkuId) {
		if (mode.value === 'update') {
			const relatedEvents = selectedSKUIdsForStore
				.value(storeId.value)
				.map((skuID) => getMatchingEventForSkuId(event, skuID, store.value?.id))
				.filter((event) => event !== undefined)

			updateRelatedEvents(relatedEvents)

			relatedEvents.forEach((event) => setCustomDepth(event.unique_sku_id, event.discount_depth ?? 0))
		} else {
			setCustomDepth(skuId, event.discount_depth ?? 0)
		}

		if (mode.value === 'create') {
			const skusForTheEvent = getSKUIdsForTheEvent(skuId)

			setSelectedSKUIds(skusForTheEvent)
		}

		updateSourceEvent(event)

		if (storeId.value && isCustomerCreatedEvent(event) && mode.value === 'update') {
			// It's possible that we are editing an event which was created before we introduced
			// store-specific rules for event start/end hours and therefore such event can have invalid
			// time which cannot be changed (because in some cases - Nintendo Switch Europe/Australia
			// - editing end hour is blocked). So, to overcome that we update start and end time of
			// edited event to match the store-specific rules.
			await fetchDiscountConfig(storeId.value, event.start_date, event.end_date)

			updateEventTimes(
				adjustDateToRules(event.start_date, discountConfig.value.startTimeRules, discountConfig.value.ianaTimeZoneName, 'startDate'),
				adjustDateToRules(event.end_date, discountConfig.value.endTimeRules, discountConfig.value.ianaTimeZoneName, 'endDate')
			)
		} else {
			updateEventTimes(event.start_date, event.end_date)
		}

		await refreshRemoteAndLocalData('all')

		isInitializingEvent.value = false
	}

	/**
	 * Create an event by clicking the `ADD EVENT` button
	 */
	async function createEvent(aStore: Store) {
		initSidebar(aStore, 'create')

		autoSelectLoneProduct()

		const opportunity = findOpportunityForSingleProduct()

		if (opportunity) {
			// Switch to an opportunity if only one SKU is selected and there is an upcoming opportunity for it
			changeToOpportunity(opportunity)

			return
		}

		// Use nearest possible start date for the event
		await fetchNearestStartDate(aStore.id)

		setStartDate(nearestStartDate.value)

		// Fetch discount config and recommended dates and update local date recommendations
		await refreshRemoteAndLocalData('all')

		// Use optimal event length (adjusts the end date)
		setOptimalDuration()

		// Update local date recommendations after end date was changed
		updateRecommendedAndOptimalDurations(discountConfig.value, eventDurations.value)
		updateRecommendedDates()

		isInitializingEvent.value = false
	}

	/**
	 * Create an event by clicking a special opportunity event at the top of the timeline.
	 */
	async function createEventFromSpecialOpportunity(aStore: Store, opportunity: EventData) {
		initSidebar(aStore, 'create', EventType.Discount, opportunity.name ?? '')

		updateSourceEvent(opportunity)

		updateEventTimes(opportunity.start_date, opportunity.end_date)

		// Fetch all data for the created event
		await refreshRemoteAndLocalData('all')

		isInitializingEvent.value = false
	}

	/**
	 * Clicked on a major or minor opportunity on the timeline.
	 */
	async function createEventFromOpportunity(aStore: Store, event: EventData, skuId: SkuId) {
		initSidebar(aStore, 'create', event.event_type, event.name ?? '')

		eventNotes.setNotes([])

		await initEventBasedOnAnotherEvent(event, skuId)
	}

	/**
	 * Clicked on the timeline on an existing, scheduled (not scraped/planned) event in the future.
	 */
	async function editEvent(aStore: Store, event: EventData, skuId: SkuId) {
		initSidebar(aStore, 'update', event.event_type, event.name ?? '')

		selectAllRelatedSkus(event, aStore.id)

		eventNotes.setNotes(cloneDeep(event.notes ?? []))

		await initEventBasedOnAnotherEvent(event, skuId)
	}

	/**
	 * Clicked on the timeline on an existing event that is either scraped/planned or in the past.
	 */
	async function viewEvent(aStore: Store, event: EventData, skuId: SkuId) {
		initSidebar(aStore, 'view', event.event_type, event.name ?? '')

		eventNotes.setNotes(cloneDeep(event.notes ?? []))

		await initEventBasedOnAnotherEvent(event, skuId)
	}

	/**
	 * Clicked on an empty cell on the timeline.
	 * @param sku SKU assigned to clicked cell
	 * @param dateOfStart Clicked cell
	 * @param dateOfEnd Is not empty if this was actually a drag event on the timeline (which is currently disabled)
	 */
	async function createEventFromEmptySlot(aStore: Store, sku: SkuData, dateOfStart: DateTime, dateOfEnd?: DateTime) {
		initSidebar(aStore, 'create')

		const skuIdsForTheEvent = getSKUIdsForTheEvent(sku.unique_sku_id)

		setSelectedSKUIds(skuIdsForTheEvent)

		// Use nearest possible start date for the event
		await fetchNearestStartDate(aStore.id)

		if (isDateTimeBefore(dateOfStart, nearestStartDate.value)) {
			// Clicked before nearest possible start date.
			// Set start date as nearest possible. Then set the given end date (if not empty) with the same hour as start date
			setStartDate(nearestStartDate.value)
		} else {
			// Use the clicked date as start date, but use default hour
			setStartDate(setSourcesHourInTarget(nearestStartDate.value, dateOfStart, discountConfig.value.ianaTimeZoneName))
		}

		// Fetch all data for the created event
		await refreshRemoteAndLocalData('all')

		// If end date is valid then use it, but with default hour. If end date is empty then use
		// optimal length for this event
		if (dateOfEnd?.isValid) {
			// set from dateOfEnd while setting hour/minute/seconds to defaults from discountConfig
			setEndDate(adjustDateToRules(dateOfEnd, discountConfig.value.endTimeRules, discountConfig.value.ianaTimeZoneName, 'endDate'))
		} else {
			setOptimalDuration()
		}

		// Update local date recommendations after end date was changed
		updateRecommendedAndOptimalDurations(discountConfig.value, eventDurations.value)
		updateRecommendedDates()

		isInitializingEvent.value = false
	}

	/**
	 * Any event is clicked on the timeline.
	 */
	async function updateEventOrCreateFromExisting(aStore: Store, event: EventData, skuId: SkuId) {
		if (!isEventType(event, [EventType.StoreOpportunity, EventType.Discount, EventType.Opportunity])) {
			return
		}

		if (isOpportunityEvent(event)) {
			await createEventFromOpportunity(aStore, event, skuId)

			return
		}

		if (useEvent(ref(event)).isEditable.value) {
			await editEvent(aStore, event, skuId)

			return
		}

		await viewEvent(aStore, event, skuId)
	}

	async function updateStartDate(date: DateTime) {
		if (store.value && date.isValid) {
			setStartDate(date)

			await onDatesChanged()
		}
	}

	async function updateEndDate(date: DateTime) {
		if (store.value && date.isValid) {
			setEndDate(date)

			await onDatesChanged()
		}
	}

	/**
	 * Indicates that event(s) for given store were added/changed and timeline for specific SKUs
	 * in that store needs to be refreshed.
	 */
	async function updateSKUData(skuIds: SkuId[], selectedStore: Store) {
		// Enable the loading state on actions button/panel until events and actions are reloaded
		indicatePendingRefresh()

		await Promise.allSettled(
			skuIds.map((skuId) => {
				if (selectedStore?.id) {
					return loadSKUData(selectedStore.id, skuId, true)
				}

				return null
			})
		)

		// Update actions once any SKU data is updated
		// In theory this could be optimized to update the actions with per-SKU granularity,
		// but for now we'll settle with single global update and then we'll see if this needs
		// any improvements
		fetchActions()

		// Refresh score after any change of events from stores which support summary calculation
		if (selectedStore?.id && storesWithSummaryIds.value.includes(selectedStore.id)) {
			fetchSaleDaysSummary()
		}
	}

	/**
	 * If optimal duration for this event is known then set it. Else, use default event duration for
	 * current store.
	 */
	function setOptimalEventDuration() {
		trackEvent('app', 'Set max possible duration for event.', { store: store.value?.displayName })

		setOptimalDuration()
	}

	/**
	 * (Re)fetches all remote data needed for the event creation or update depending on what inputs
	 * have changed and on type of current event.
	 * It also updates local state in event details stores based on fetche data.
	 */
	async function refreshRemoteAndLocalData(changedInput: 'products' | 'dates' | 'all') {
		if (!storeId.value) {
			return
		}

		const asyncOperations: Promise<unknown>[] = []

		if (changedInput === 'all' || changedInput === 'dates') {
			asyncOperations.push(fetchDiscountConfig(storeId.value, startDate.value, endDate.value))
		}

		if ((changedInput === 'all' || changedInput === 'dates') && !isReadonly.value) {
			asyncOperations.push(fetchRecommendedDepths(storeId.value, startDate.value, endDate.value))
		}

		const { eventsForSelectedSkus, cooldownsForSelectedSkus, discountsForDatesRecommendations } = getEventsPayloads()

		if (!isFromMajorStoreOpportunity.value && isUpdateEvent.value) {
			// Recommended dates are fetch only when editing an event which is not a major store event.
			// When a new event is created we use local timeline context to come up with recommended dates.
			asyncOperations.push(fetchRecommendedDates(storeId.value, startDate.value, endDate.value, discountsForDatesRecommendations))
		}

		if (!isFromMajorStoreOpportunity.value && !isReadonly.value) {
			asyncOperations.push(
				fetchEventMaxAndOptimalLengths(
					storeId.value,
					startDate.value,
					endDate.value,
					selectedSkuIds.value,
					eventsForSelectedSkus,
					cooldownsForSelectedSkus
				)
			)
		}

		await Promise.allSettled(asyncOperations)

		// Update local data based on fetched remote data
		updateRecommendedAndOptimalDurations(discountConfig.value, eventDurations.value)
		updateRecommendedDates()
	}

	async function onDatesChanged() {
		if (isInitializingEvent.value || !isEventSidebarVisible.value) {
			return
		}

		await refreshRemoteAndLocalData('dates')
	}

	async function onProductsChanged() {
		if (isInitializingEvent.value || !isEventSidebarVisible.value) {
			return
		}

		await refreshRemoteAndLocalData('products')
	}

	function reset() {
		isInitializingEvent.value = true
		isSaving.value = false
		type.value = undefined

		if (storeId.value) {
			unselectAllSKUs()
		}

		resetBaseStore()
		resetRemoteData()
		resetDates()
		resetDepths()
		resetDurations()

		eventNotes.cancel(true)
	}

	watch(selectedSkuIds, (ids, previousIds) => {
		if (!ids || ids.length === 0 || !isEventSidebarVisible.value) {
			return
		}

		if (!previousIds || !arraysHaveTheSameElements(ids, previousIds)) {
			onProductsChanged()
		}
	})

	return {
		createEvent,
		createEventFromSpecialOpportunity,
		createEventFromEmptySlot,
		updateEventOrCreateFromExisting,
		updateStartDate,
		updateEndDate,
		changeToOpportunity,
		updateCustomDiscountDepth: setCustomDepth,
		setOptimalEventDuration,
		submit,
		deleteEvent,
		reset,

		name,
		products,
		startDate,
		endDate,
		type,
		store,
		storeSKUs,
		sourceEvent,
		sourceOpportunity,
		sourceOpportunityDescription,
		isSaving: computed(() => isSaving.value),
		isUpdateEvent,
		isReadonly,
		isRecommendedEventDuration,
		optimalEventDurationInDays: optimalDurationInDays,
		optimalEventDurationFormatted,
		maxEventDurationInDays: maxDurationInDays,
		isOptimalEventDurationValid,
		maxEventDurationFormatted,
		eventDuration,
		isEventTooLong,
		isEventInThePast,
		cooldownDuration,
		cooldownStart,
		cooldownStartRaw,
		cooldownEnd,
		cooldownEndRaw,
		isCooldownLengthValid,
		doesEventTriggerCooldown,
		isFromOpportunity,
		isFromStoreOpportunity,
		isFromMajorStoreOpportunity,
		recommendedStartDate,
		recommendedEndDate,
		isEventLongerThanOptimal,
		recommendedDiscountDepth: recommendedDepths,
		eventIntervalUTC,
		sourceEventsCooldowns: sourceEventCooldowns,
		eventDatesDraft,
		canCalculateEventParameters,
		discountConfig,
		isScraped
	}
})
